import React, { Fragment } from 'react';
import { TransportCard } from './transport-card';
import { ActivityCard } from './activity-card';
import { ArrivalCard } from './arrival-card';
import { DepartureCard } from './departure-card';
import { TransportPrompt } from './transport-prompt';
import { ItinerarySurveyCard } from '../itinerary-component/ItinerarySurveyCard';
import type { TimelineItem } from '../itinerary-component/ItineraryBody';
import { formatDateInLocale } from '@fshr/shared';
import type { FSLocale } from '@fshr/shared';
import { useTranslations } from '@fshr/context/i18n-context';

interface TimelineProps {
	bookingId: string;
	locale?: string;
	data: {
		timelineItems: TimelineItem[];
		itinerary: {
			hasRequestedDepartureTransportation: boolean;
			requestDepartureTransportationUrl: string;
			hasRequestedArrivalTransportation: boolean;
			requestArrivalTransportationUrl: string;
		};
		booking?: {
			checkOutDate: string;
			checkInDate: string;
			propertyName: string;
		};
		webchat: {
			enabled: boolean;
		};
	};
	survey?: {
		enabled: boolean;
		createSurveyLink: (rating: number) => string;
		onSurveyInteraction: () => void;
		onDismiss: () => void;
	};
}

export const Timeline: React.FC<TimelineProps> = ({ data, locale, survey }) => {
	const hasDepartureTransport = data?.itinerary?.hasRequestedDepartureTransportation;
	const hasArrivalTransport = data?.itinerary?.hasRequestedArrivalTransportation;
	const requestSubtypes = ['Arrival Transportation', 'Departure Transportation'];
	const t = useTranslations();

	return (
		<section
			className="flex flex-col p-300 border-b-[1px] border-b-border-subtle"
			aria-label={t('Your Itinerary Timeline', [])}
		>
			<h2 className="text-h4 mb-200">{t('Your Itinerary Timeline', [])}</h2>
			<div role="list">
				{/* Arrival Transport Prompt */}
				{!hasArrivalTransport && (
					<TransportPrompt
						type="arrival"
						url={data?.itinerary.requestArrivalTransportationUrl}
						date={formatDateInLocale(
							data?.booking?.checkInDate ?? '',
							'MMM d',
							'yyyy-MM-dd',
							locale as FSLocale
						)}
					/>
				)}
				{data?.timelineItems?.map(({ date, item }: TimelineItem, index: number) => {
					const id = `${date}-${index}`;
					const formattedDate = formatDateInLocale(date, 'MMM d', 'yyyy-MM-dd', locale as FSLocale);
					const formattedTime =
						item.time &&
						formatDateInLocale(item.time, 'h:mma', "HH:mm:ss.SSS'Z'", locale as FSLocale);
					if (item.type === 'arrival') {
						// Find first arrival card index
						const firstArrivalIndex = data.timelineItems.findIndex(
							timelineItem => timelineItem.item.type === 'arrival'
						);
						const isFirstArrival = index === firstArrivalIndex;

						return (
							<Fragment key={`arrival-${id}`}>
								<ArrivalCard
									date={formattedDate}
									time={item.checkInTime}
									vendor={item.vendorNameOnItinerary}
									status={item.status}
									roomType={item.roomType}
									averageDailyRate={item.averageDailyRate}
									totalRoomRate={item.totalRoomRate}
									estimatedTotal={item.estimatedTotal}
									disclaimer={item.disclaimer}
									currencyCode={item.currencyCode}
									dailyRateDisclaimer={item.dailyRateDisclaimer}
									offer={item.offer}
								/>
								{/* Itinerary Survey */}
								{isFirstArrival && survey?.enabled && (
									<ItinerarySurveyCard
										createSurveyLink={survey.createSurveyLink}
										onSurveyInteraction={survey.onSurveyInteraction}
										onDismiss={survey.onDismiss}
									/>
								)}
							</Fragment>
						);
					}

					if (
						item.type === 'Transportation' &&
						item?.requestSubtype &&
						requestSubtypes.includes(item.requestSubtype)
					) {
						return (
							<TransportCard
								key={`transport-${id}`}
								date={formattedDate}
								time={formattedTime}
								vendor={item.vendorNameOnItinerary}
								status={item.status}
								description={item.description}
								totalCost={item.totalCost}
								vehicleSelected={item.vehicleSelected}
								pickupLocation={item.pickupLocation || ''}
								dropoffLocation={item.dropoffLocation ?? ''}
								guests={item.numberOfGuests}
								notes={item.guestVisibleNotes}
								currencyCode={item.currencyCode}
								cancellationPolicy={item.cancellationPolicy}
								luggage={item.numberOfItems}
								confirmationNumber={item.confirmationNumber}
								chatEnabled={data.webchat.enabled}
								title={item?.requestSubtype}
							/>
						);
					}

					return (
						<ActivityCard
							key={`activity-${id}`}
							date={formattedDate}
							time={formattedTime}
							vendor={item.vendorNameOnItinerary}
							status={item.status}
							title={item.productName}
							type={(item.type?.toLowerCase() || item.requestType?.toLowerCase() || '') as any}
							totalCost={item.totalCost}
							guests={item.totalGuests}
							quantity={item.numberOfGuests || item.quantity?.toString()}
							duration={item.duration}
							description={item.description}
							notes={item.guestVisibleNotes}
							currencyCode={item.currencyCode}
							confirmationNumber={item.confirmationNumber}
							departureLocation={item.departureLocation}
							cancellationPolicy={item.cancellationPolicy}
							preferredTimeOfDay={item.preferredTimeOfDay}
							chatEnabled={data.webchat.enabled}
						/>
					);
				})}

				{/* Departure Transport Prompt */}
				{!hasDepartureTransport && (
					<TransportPrompt
						type="departure"
						url={data?.itinerary.requestDepartureTransportationUrl}
						date={formatDateInLocale(
							data?.booking?.checkOutDate ?? '',
							'MMM d',
							'yyyy-MM-dd',
							locale as FSLocale
						)}
					/>
				)}
				{/* Departure Card */}
				<DepartureCard />
			</div>
		</section>
	);
};
